import React, { Fragment, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import DatePicker from 'react-datepicker';
import startOfDay from 'date-fns/startOfDay';

import FormGroup from '../../../../../../../components/global/FormGroup';
import Alert from '../../../../../../../components/global/Alert';
import {
  getFormikTouchedErrors,
  resetFormikFields,
} from '../../../../../../../util/formik';
import Label from '../../../../../../../components/global/Label';
import Select from '../../../../../../../components/global/Select';
import { EVENT_SETTING_ANNIVERSARY } from '../../../../../../../constants/album-folder';
import { capitalize } from '../../../../../../../util/text';
import InputGroup from '../../../../../../../components/global/InputGroup';
import { getAsset } from '../../../../../../../util/assets';
import { EVENT_DETAILS_CONTEXT } from '../utils';

const propTypes = {
  user: PropTypes.shape({
    id: PropTypes.string,
    email_address: PropTypes.string,
  }),
  values: PropTypes.shape({
    name: PropTypes.string,
    date: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    anniversary_type: PropTypes.string,
    event_date: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  }).isRequired,
  touched: PropTypes.object,
  errors: PropTypes.object,
  handleChange: PropTypes.func.isRequired,
  handleBlur: PropTypes.func,
  context: PropTypes.oneOf(Object.values(EVENT_DETAILS_CONTEXT)),
};

const AnniversaryEventDetails = ({
  values,
  touched,
  errors,
  handleChange,
  handleBlur,
  context,
}) => {
  const dateInputRef = useRef();
  const errorList = Object.values(getFormikTouchedErrors(errors, touched));
  const { date } = values;

  useEffect(() => {
    if (dateInputRef?.current) {
      dateInputRef.current.readOnly = 'readonly';
    }
  }, [dateInputRef]);

  return (
    <Fragment>
      <FormGroup
        id="name"
        name="name"
        label={
          context === EVENT_DETAILS_CONTEXT.SLIDESHOW
            ? 'Slideshow Name'
            : 'Event Name'
        }
        placeholder="Enter event name here..."
        onChange={handleChange}
        onBlur={handleBlur}
        value={values.name}
        error={!!errors.name}
      />
      <div className="row">
        <div className="col-12">
          <Select
            id="anniversary_type"
            name="anniversary_type"
            label="Type of anniversary"
            value={values.anniversary_type}
            error={!!errors.anniversary_type}
            className="mb-2"
            onChange={event => {
              handleChange(event);

              resetFormikFields(handleChange, [
                'groom_name',
                'bride_name',
                'first_name',
                'last_name',
                'wedding_year',
                'work_start_year',
              ]);
            }}
          >
            <option value="">Select one</option>
            {Object.keys(EVENT_SETTING_ANNIVERSARY).map(type => (
              <option key={type} value={type}>
                {capitalize(type)}
              </option>
            ))}
          </Select>
          {values.anniversary_type === EVENT_SETTING_ANNIVERSARY.WORK && (
            <div className="row">
              <FormGroup
                id="first_name"
                name="first_name"
                label="Name of the celebrated"
                placeholder="First name"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.first_name}
                className="col-12 col-lg-6 mb-1"
                error={!!errors.first_name}
              />
              <FormGroup
                id="last_name"
                name="last_name"
                label={<>&nbsp;</>}
                labelProps={{
                  className: 'd-none d-lg-block',
                }}
                className="col-12 col-lg-6"
                placeholder="Last name"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.last_name}
                error={!!errors.last_name}
              />
            </div>
          )}
          {values.anniversary_type === EVENT_SETTING_ANNIVERSARY.WEDDING && (
            <div className="row">
              <FormGroup
                id="groom_name"
                name="groom_name"
                label="Whose anniversary is being celebrated?"
                placeholder="Bride / Groom's first name"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.groom_name}
                className="freemium-bride-name-form-group col-12 col-lg-6"
                error={!!errors.groom_name}
              />
              <div className="col-12 d-lg-none text-center">&amp;</div>
              <FormGroup
                id="bride_name"
                name="bride_name"
                label={<>&nbsp;</>}
                labelProps={{
                  className: 'd-none d-lg-block',
                }}
                className="col-12 col-lg-6"
                placeholder="Bride / Groom's first name"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.bride_name}
                error={!!errors.bride_name}
              />
            </div>
          )}
        </div>
        {values.anniversary_type === EVENT_SETTING_ANNIVERSARY.WORK && (
          <FormGroup
            id="work_start_year"
            name="work_start_year"
            label="Starting year"
            placeholder="Please specify starting year..."
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.work_start_year}
            className="col-12 col-lg-6 mb-1"
            type="number"
            error={!!errors.work_start_year}
          />
        )}
        <div
          className={
            values.anniversary_type === EVENT_SETTING_ANNIVERSARY.WORK
              ? 'col-12 col-lg-6'
              : 'col-12'
          }
        >
          <div className="form-group">
            <Label id="date" optional>
              Having a party?
            </Label>
            <div className="datepicker-gray-light">
              <DatePicker
                id="date"
                required
                name="date"
                customInput={
                  <InputGroup
                    id="date"
                    name="date"
                    error={!!errors.date}
                    type="text"
                    autoComplete="off"
                    inputRef={dateInputRef}
                    addon={
                      <img
                        alt=""
                        src={getAsset('/shutterbug/icons/calendar.svg')}
                      />
                    }
                  />
                }
                popperClassName="freemium-event-date-picker-popper"
                wrapperClassName="freemium-event-date-picker"
                onChange={selectedDate => {
                  handleChange({
                    target: {
                      name: 'date',
                      value: startOfDay(selectedDate),
                    },
                  });
                }}
                selected={date}
              />
            </div>
          </div>
        </div>
      </div>

      {errorList.length > 0 && (
        <Alert type="danger" className="mt-1 text-normal">
          <ul className="mb-0">
            {errorList.map(error => (
              <li key={error}>{error}</li>
            ))}
          </ul>
        </Alert>
      )}
    </Fragment>
  );
};

AnniversaryEventDetails.propTypes = propTypes;

export default AnniversaryEventDetails;
